import { useQuery, useMutation, useQueryClient } from "react-query";
import { useContext } from "react";
import { FeatureAccessContext } from "@/context/featureAccess";
import { useApi, apiResolver } from "@/api/useApi";
import { NotificationTriggerType, isNotificationTriggerType } from "./NotificationTriggers.model";

export interface SlackNotificationSettings {
  id: string;
  enabled: boolean;
  channelId?: string | null;
  trigger: NotificationTriggerType;
  tenantId?: string;
  createdAt?: string;
  updatedAt?: string;
}

export function useSlackNotificationSettings() {
  const { selectedWorkspace } = useContext(FeatureAccessContext);
  const { api } = useApi();

  return useQuery<SlackNotificationSettings[]>({
    queryKey: ["slack-notification-settings", selectedWorkspace],
    queryFn: async () => {
      const data = await apiResolver<{ settings: SlackNotificationSettings[] }>(
        api.get("/platform/slack-notifications/settings")
      );
      return data.settings;
    },
    enabled: !!selectedWorkspace,
  });
}

export function useUpdateSlackNotificationSetting() {
  const { selectedWorkspace } = useContext(FeatureAccessContext);
  const { api } = useApi();
  const queryClient = useQueryClient();

  return useMutation<
    void,
    Error,
    { trigger: string; enabled?: boolean; channelId?: string; timezone?: string }
  >({
    mutationFn: async ({ trigger, enabled, channelId, timezone }) => {
      // Validate trigger before sending to API
      if (!isNotificationTriggerType(trigger)) {
        throw new Error(`Invalid trigger type: ${trigger}`);
      }
      
      const payload: { trigger: NotificationTriggerType; enabled?: boolean; channelId?: string; timezone?: string } = {
        trigger,
      };
      if (enabled !== undefined) payload.enabled = enabled;
      if (channelId !== undefined) payload.channelId = channelId;
      if (timezone !== undefined) payload.timezone = timezone;

      return apiResolver<void>(
        api.post(`/platform/slack-notifications/settings`, payload)
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries(["slack-notification-settings", selectedWorkspace]);
    },
  });
}
